import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Card } from '@asia/shared';
import { splitAmount } from '../../lib/format';
import { copyText } from '../../lib/clipboard';
import { haptic } from '../../hooks/useTelegram';
import { FigIcon } from '../FigIcon';
import cardFace from '../../assets/figma/cards/card-face.png';
import mastercard from '../../assets/icons/mastercard.svg';
import visaWhite from '../../assets/icons/visa-white.svg';

function Brand({ card }: { card: Card }) {
  return (
    <div className="absolute right-[5.4%] top-[9.3%] flex h-[15.3%] items-center">
      <img
        src={card.brand === 'mastercard' ? mastercard : visaWhite}
        alt={card.brand === 'mastercard' ? 'Mastercard' : 'VISA'}
        className="h-full w-auto"
      />
    </div>
  );
}

/** Virtual VISA / Mastercard card. The blue gradient, dotted world map and
 *  "ASIA WALLET" wordmark are the exact Figma render (node 2:5813, exported at 3×
 *  with balance/brand stripped); balance, number and brand are overlaid, brand
 *  follows `card.brand`.
 *
 *  When `flippable`, tapping asks to reveal the PAN / expiry / CVV — the caller
 *  decides, since seeing them needs a PIN or biometric check first. Without
 *  `onReveal` the tap flips straight away (previews with nothing to protect).
 *  The flip
 *  is a single always-visible panel doing two half-turns (0→90°, swap content,
 *  −90°→0). No `backface-visibility`, no preserve-3d, no second face — so no
 *  browser can ever paint a white backing behind it; the only thing rotating is
 *  the opaque card itself over the page background.
 *
 *  `creditState` drives an in-place top-up animation on this face (list card),
 *  not a separate overlay copy of the card. */
export interface CardFaceHandle {
  /** Flip to reveal / hide the PAN / expiry / CVV — same as tapping the card. */
  flip: () => void;
}

export type CardCreditState = 'idle' | 'pending' | 'done';

/** Group a card number in fours. Returns null unless it really is one — an empty
 *  or partial value must never be mistaken for a number the user can read. */
function formatPan(pan: string | undefined): string | null {
  const digits = (pan ?? '').replace(/\D/g, '');
  if (digits.length < 12) return null;
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}

/** Digits-only PAN for clipboard paste into payment forms. */
function panDigits(pan: string | undefined): string | null {
  const digits = (pan ?? '').replace(/\D/g, '');
  return digits.length >= 12 ? digits : null;
}

/** Tap-to-copy chip on the card back. Stops the flip handler on the face. */
function CopyField({
  label,
  value,
  copyValue,
  valueClassName,
}: {
  label: string;
  value: string;
  /** What goes on the clipboard — omit when there's nothing real to copy. */
  copyValue?: string | null;
  valueClassName: string;
}) {
  const { t } = useTranslation();
  const canCopy = Boolean(copyValue);
  const onCopy = (e: MouseEvent) => {
    e.stopPropagation();
    if (!copyValue) return;
    void copyText(copyValue);
  };
  return (
    <button
      type="button"
      disabled={!canCopy}
      onClick={onCopy}
      aria-label={canCopy ? `${t('common.copy')}: ${label}` : label}
      className={`flex flex-col gap-[1.5cqw] text-left ${canCopy ? 'active:opacity-70' : ''}`}
    >
      <span className="text-[3.24cqw] uppercase tracking-wide text-white/[0.44]">{label}</span>
      <span className="flex items-center gap-[1.5cqw]">
        <span className={`leading-none tabular-nums ${valueClassName}`}>{value}</span>
        {canCopy ? (
          <FigIcon name="copy" size={16} className="shrink-0 text-white/[0.55]" />
        ) : null}
      </span>
    </button>
  );
}

export const CardFace = forwardRef<
  CardFaceHandle,
  {
    card: Card;
    className?: string;
    flippable?: boolean;
    /** Tapping asks the owner to reveal — seeing the number needs confirmation. */
    onReveal?: () => void;
    /** Show the back. The owner sets it only once the real number is in hand, so
     *  the card can never turn over onto an empty face. */
    revealed?: boolean;
    /** In-list top-up motion on this face. */
    creditState?: CardCreditState;
    /** e.g. "+12 USDT" under the balance while credit is pending. */
    creditHint?: string | null;
  }
>(function CardFace(
  {
    card,
    className = '',
    flippable = false,
    onReveal,
    revealed,
    creditState = 'idle',
    creditHint,
  },
  ref,
) {
  const { t } = useTranslation();
  const [showBack, setShowBack] = useState(false);
  const [angle, setAngle] = useState(0);
  const [snap, setSnap] = useState(false); // true = move without transition
  const busy = useRef(false);

  const { int, frac } = splitAmount(card.balanceUsd);
  const frozen = card.status === 'frozen';
  const pending = card.status === 'pending';
  const expiry = `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).padStart(2, '0')}`;
  const pan = formatPan(card.number);
  const number = pan ?? `•••• •••• •••• ${card.last4 ?? '••••'}`;
  const crediting = creditState === 'pending';
  const credited = creditState === 'done';
  // Pending cards have no PAN yet — don't invite a reveal that only hits the provider.
  const canFlip = flippable && creditState === 'idle' && !pending;

  // Timer-driven (not transitionend, which browsers drop when the tab or layer
  // is throttled and would leave the card stuck mid-flip): first half-turn to
  // 90° (edge-on, invisible), swap sides + jump to −90 with no transition, then
  // animate back to 0.
  const turnTo = (back: boolean) => {
    if (busy.current || back === showBack) return;
    busy.current = true;
    haptic();
    setAngle(90);
    setTimeout(() => {
      setSnap(true);
      setShowBack(back);
      setAngle(-90);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          setSnap(false);
          setAngle(0);
          setTimeout(() => {
            busy.current = false;
          }, 320);
        }),
      );
    }, 310);
  };

  // Which side is up is decided by the owner, not in here: it only turns the
  // card over once it actually holds the number to show.
  useEffect(() => {
    if (revealed === undefined) return;
    turnTo(revealed);
    // turnTo reads current state each time; re-running on `revealed` is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  // Kept for callers that still flip directly (previews with nothing to hide).
  useImperativeHandle(ref, () => ({ flip: () => turnTo(!showBack) }));

  return (
    /* The wrapper paints NOTHING (no bg, no shadow) — while the card rotates,
       only the page background is behind it. The rounded corners and drop
       shadow live on the rotating panel itself so the shadow turns with the
       card instead of ghosting as a static card-shaped ring. */
    <div
      className={`relative aspect-[370/216] w-full ${canFlip ? 'cursor-pointer' : ''} ${className}`}
      style={{ perspective: '1400px' }}
      onClick={canFlip ? (onReveal ?? (() => turnTo(!showBack))) : undefined}
    >
      {crediting ? (
        <div
          aria-hidden
          className="card-credit-aura pointer-events-none absolute -inset-[6%] rounded-[44px]"
        />
      ) : null}
      <div
        className={`relative h-full w-full overflow-hidden rounded-[36px] text-white shadow-balance [container-type:inline-size] ${
          snap ? '' : 'transition-transform duration-300 ease-in'
        } ${credited ? 'card-credit-settle' : ''}`}
        style={{ transform: `rotateY(${angle}deg)` }}
      >
        <img src={cardFace} alt="" className="absolute inset-0 h-full w-full object-cover" />
        {crediting ? (
          <>
            <div
              aria-hidden
              className="card-credit-sheen pointer-events-none absolute inset-0 rounded-[36px]"
            />
            <div
              aria-hidden
              className="card-credit-rim pointer-events-none absolute inset-0 rounded-[36px]"
            />
          </>
        ) : null}
        {/* Always mounted so freeze/unfreeze/pending cross-fades smoothly instead
            of the overlay snapping in and out. */}
        <div
          className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-[10cqw] bg-card/25 backdrop-blur-[1px] transition-opacity duration-300 ${
            frozen || pending ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <FigIcon
            name={pending ? 'pfLock' : 'pfSun'}
            size={44}
            className="text-white drop-shadow-[0px_2px_6px_rgba(0,0,0,0.25)]"
          />
          <span className="text-[14px] font-semibold uppercase tracking-wide text-white drop-shadow-[0px_1px_4px_rgba(0,0,0,0.3)]">
            {pending ? t('cards.pending') : t('cards.frozen')}
          </span>
        </div>
        <Brand card={card} />

        {!showBack ? (
          /* ── front: balance, with the card's name in the opposite corner ── */
          <div className="absolute inset-x-[5.4%] bottom-[8.5%] flex items-end justify-between gap-[4%]">
            <div className={credited ? 'card-credit-balance-pop' : crediting ? 'card-credit-balance-wait' : ''}>
              <p className="text-[3.78cqw] font-medium text-white/[0.44]">
                {crediting ? t('cards.toppingUpShort') : t('cards.balance')}
              </p>
              <p className="mt-[1cqw] font-bold leading-none">
                <span className="text-[10.27cqw] text-white/[0.66]">$</span>
                <span className="text-[10.27cqw]">{int}</span>
                <span className="text-[6.49cqw] text-white/[0.66]">.{frac}</span>
              </p>
              {crediting && creditHint ? (
                <p className="card-credit-hint mt-[1.2cqw] text-[3.5cqw] font-semibold tracking-[-0.2px] text-[#8fe0b0]">
                  {creditHint}
                </p>
              ) : null}
            </div>
            <span className="max-w-[48%] truncate text-[4.05cqw] font-medium leading-none text-white/[0.72]">
              {card.label}
            </span>
          </div>
        ) : (
          /* ── back: full PAN / expiry / CVV — tap a field to copy ── */
          <>
            <div className="absolute inset-x-[5.4%] top-[36%]">
              <CopyField
                label={t('cards.cardNumber')}
                value={number}
                copyValue={panDigits(card.number)}
                valueClassName="text-[5.4cqw] tracking-[0.08em]"
              />
            </div>
            <div className="absolute inset-x-[5.4%] bottom-[8.5%] flex items-end gap-[10%]">
              <CopyField
                label={t('cards.expiry')}
                value={expiry}
                copyValue={card.expMonth && card.expYear ? expiry : null}
                valueClassName="text-[4.6cqw]"
              />
              <CopyField
                label="CVV"
                value={card.cvv ?? '•••'}
                copyValue={card.cvv || null}
                valueClassName="text-[4.6cqw]"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});
